import React from 'react';
import {
  Entity,
  EditorState,
  CompositeDecorator,
  CharacterMetadata,
} from 'draft-js';
import { convertFromHTML, convertToHTML } from 'draft-convert';

/////////
// Text counters for rich text editor
/////////

export const getCharCount = editorState => {
  const plainText = editorState.getCurrentContent().getPlainText('');
  const regex = /(?:\r\n|\r|\n)/g;
  return plainText.replace(regex, '').trim().length;
};

export const getLineCount = editorState => {
  const blockArray = editorState.getCurrentContent().getBlocksAsArray();
  return blockArray && blockArray.length;
};

export const getWordCount = editorState => {
  const plainText = editorState.getCurrentContent().getPlainText('');
  const wordArray = plainText
    .replace(/(?:\r\n|\r|\n)/g, ' ')
    .trim()
    .match(/\S+/g);
  return wordArray ? wordArray.length : 0;
};

/////////
// Link rendering in rich text editor
/////////

export const findLinkEntities = (contentBlock, callback) => {
  contentBlock.findEntityRanges(character => {
    const entityKey = character.getEntity();

    return entityKey !== null && Entity.get(entityKey).getType() === 'LINK';
  }, callback);
};

type Props = {
  entityKey: string,
  children: Element,
};

export const Link = ({ entityKey, children }: Props) => {
  const { url } = Entity.get(entityKey).getData();
  return (
    <a href={url} target="_blank">
      {children}
    </a>
  );
};

export const linkDecorator = new CompositeDecorator([
  {
    strategy: findLinkEntities,
    component: Link,
  },
]);

/////////
// html parsing/rendering from/to draft js editor state
////////

export const stateToHTML = editoContent => {
  return convertToHTML({
    styleToHTML: style => {
      if (style && style.substring(0, 3) === 'rgb') {
        return <span style={{ color: style }} />;
      }
    },
    blockToHTML: block => {
      const blockStyles = block.inlineStyleRanges;

      const alignment = blockStyles.find(
        style =>
          style.style === 'left' ||
          style.style === 'center' ||
          style.style === 'right'
      );

      switch (block.type) {
        case 'unstyled':
          return <p style={{ textAlign: alignment && alignment.style }} />;
        case 'blockquote':
          return (
            <blockquote style={{ textAlign: alignment && alignment.style }} />
          );
        case 'code-block':
          return <pre style={{ textAlign: alignment && alignment.style }} />;
        default:
          return null;
      }
    },
    entityToHTML: (entity, originalText) => {
      if (entity.type === 'LINK') {
        return <a href={entity.data.url}>{originalText}</a>;
      }
      return originalText;
    },
  })(editoContent);
};

export const htmlToState = html => {
  const decorator = new CompositeDecorator([
    { strategy: findLinkEntities, component: Link },
  ]);

  const state = convertFromHTML({
    htmlToStyle: (nodeName, node, currentStyle) => {
      let patchedStyle = currentStyle;

      if (node.style.color) {
        const existingColor = patchedStyle.find(
          style => style && style.substring(0, 3) === 'rgb'
        );
        if (existingColor) {
          patchedStyle = patchedStyle.delete(existingColor);
        }
        patchedStyle = patchedStyle.add(node.style.color);
      }

      if (node.style.textAlign) {
        patchedStyle = patchedStyle.add(node.style.textAlign);
      }

      return patchedStyle;
    },
    htmlToEntity: (nodeName, node) => {
      if (nodeName === 'a') {
        return Entity.create('LINK', 'MUTABLE', { url: node.href });
      }
    },
    htmlToBlock: (nodeName, node) => {
      if (nodeName === 'blockquote') {
        return {
          type: 'blockquote',
          data: {},
        };
      }
    },
  })(html);

  const applyAlignmentStyles = contentBlock => {
    return contentBlock
      .getCharacterList()
      .map(c =>
        CharacterMetadata.applyStyle(c, contentBlock.data.get('textAlign'))
      );
  };

  const applyColorStyles = contentBlock => {
    return contentBlock.getCharacterList().map(c => {
      return CharacterMetadata.applyStyle(c, contentBlock.data.get('color'));
    });
  };

  // TODO: Could proably use reduce here to chain state mutations and style applications
  const newBlockMap = state
    .getBlockMap()
    .map(contentBlock =>
      contentBlock.set('characterList', applyAlignmentStyles(contentBlock))
    );

  const alignedState = state.set('blockMap', newBlockMap);
  const coloredState = state.set(
    'blockMap',
    alignedState
      .getBlockMap()
      .map(contentBlock =>
        contentBlock.set('characterList', applyColorStyles(contentBlock))
      )
  );

  return EditorState.createWithContent(coloredState, decorator);
};

/////////
// Special Block rendering and className application on custom blocks of text
/////////

export const getBlockColor = block => {
  let color = '';

  block.findStyleRanges(e => {
    color = e
      .getStyle()
      .find(style => style && style.substring(0, 3) === 'rgb');
  });

  return color;
};

export const getBlockAlignment = block => {
  let style = 'left';
  block.findStyleRanges(e => {
    if (e.hasStyle('center')) style = 'center';
    if (e.hasStyle('right')) style = 'right';
  });
  return style;
};

/////////
// Controls rendering in rich text edito
/////////

export const BLOCK_TYPES = [
  // {label: 'Blockquote', style: 'blockquote', display_style: 'quote right'},
  { label: 'UL', style: 'unordered-list-item', display_style: 'list' },
  { label: 'OL', style: 'ordered-list-item', display_style: 'ordered list' },
  // {label: 'Code Block', style: 'code-block', display_style: 'code'}
];

export const HEADER_TYPES = [
  { label: 'Titre 1', style: 'header-one' },
  { label: 'Titre 2', style: 'header-two' },
  { label: 'Titre 3', style: 'header-three' },
  { label: 'Titre 4', style: 'header-four' },
  { label: 'Titre 5', style: 'header-five' },
  { label: 'Titre 6', style: 'header-six' },
];

export const ALIGN_TYPES = [
  {
    label: 'Align left',
    className: '.alignment--left',
    style: 'align left',
    display_style: 'align left',
  },
  {
    label: 'Align center',
    className: '.alignment--center',
    style: 'align center',
    display_style: 'align center',
  },
  {
    label: 'Align right',
    className: '.alignment--right',
    style: 'align right',
    display_style: 'align right',
  },
];

export const INLINE_STYLES = [
  { label: 'Bold', style: 'BOLD', display_style: 'bold' },
  { label: 'Italic', style: 'ITALIC', display_style: 'italic' },
  { label: 'Underline', style: 'UNDERLINE', display_style: 'underline' },
  //  {label: 'Monospace', style: 'CODE', display_style: 'text width'}
];



// WEBPACK FOOTER //
// ./src/utils/richtext.js